import { Package, Repeat, ShieldOff, CheckCircle2, GraduationCap, BadgeCheck, Star, Phone, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_SOFT = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const REASONS = [{
  Icon: Package,
  title: "Real stock on the van",
  desc: "We don't 'order it in'. Every common B&D, Merlin, Chamberlain, ATA and Steel-Line remote rides with us — programmed and tested same visit."
}, {
  Icon: Repeat,
  title: "Universals for legacy motors",
  desc: "Motor 15+ years old? Brand discontinued? We carry universal remotes (ATA TrioCodeGDO, Liftmaster, Centsys) that pair with almost anything."
}, {
  Icon: ShieldOff,
  title: "Old remotes wiped, every time",
  desc: "We don't just add a new one — we factory-reset the motor's remote memory so previous owners, ex-partners or old tradies can't open your door."
}, {
  Icon: GraduationCap,
  title: "We teach you the cheat-codes",
  desc: "Walk you through adding extra remotes yourself, changing the keypad PIN, and factory-resetting when you sell the house. No callback needed."
}, {
  Icon: BadgeCheck,
  title: "Fixed price quoted on the phone",
  desc: "Tell us your brand and what you need — we'll give you a flat all-in price before we leave the yard. No hourly games, no surprises."
}, {
  Icon: Star,
  title: "1-Year Labour Warranty",
  desc: "If the pairing fails inside 12 months, we come back and re-program — no charge, no fuss. Backed by our 5.0★ Sydney-wide service record."
}];
export default function RemoteWhyUs() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Paper grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      {/* Corner ornaments */}
      <div aria-hidden className="absolute top-10 left-10 pointer-events-none" style={{
      opacity: 0.5
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>
      <div aria-hidden className="absolute bottom-10 right-10 pointer-events-none" style={{
      opacity: 0.5,
      transform: "rotate(180deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-16 items-start">
          {/* LEFT — editorial header + stat callout */}
          <div className="lg:sticky lg:top-28">
            <div className="mb-5 flex items-center gap-3">
              <div aria-hidden className="h-px w-12" style={{
              backgroundColor: GOLD
            }} />
              <span style={{
              fontFamily: FONT_HEAD,
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: GOLD_DARK
            }}>
                06 · Why Sydney Calls Gold
              </span>
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontWeight: 800,
            fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            color: NAVY,
            marginBottom: "24px"
          }}>
              Six reasons your
              <br />
              door will{" "}
              <span style={{
              position: "relative",
              display: "inline-block",
              color: NAVY
            }}>
                click first time
                <span aria-hidden style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: "0.08em",
                height: "0.18em",
                backgroundColor: `${GOLD}55`,
                zIndex: -1
              }} />
              </span>
              .
            </h2>

            <p style={{
            fontFamily: FONT_BODY,
            fontSize: "16px",
            lineHeight: 1.7,
            color: INK_SOFT,
            marginBottom: "32px",
            maxWidth: "440px"
          }}>
              Remote programming sounds simple. It's not — every brand has its
              own quirks. Here's what we do differently so you only ever pay
              for one visit.
            </p>

            {/* Big stat callout */}
            <div className="relative rounded-2xl overflow-hidden mb-8" style={{
            background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`,
            boxShadow: "0 20px 50px rgba(8,20,56,0.25)"
          }}>
              {/* Top gold edge */}
              <div aria-hidden className="absolute top-0 left-0 right-0 h-1" style={{
              background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`
            }} />

              {/* Watermark */}
              <div aria-hidden className="absolute pointer-events-none select-none" style={{
              right: "-10px",
              bottom: "-30px",
              fontFamily: FONT_HEAD,
              fontSize: "160px",
              fontWeight: 800,
              lineHeight: 1,
              color: `${GOLD}10`,
              letterSpacing: "-0.05em"
            }}>
                06
              </div>

              <div className="relative p-8">
                <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: GOLD,
                marginBottom: "10px"
              }}>
                  Same-visit success rate
                </div>
                <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "clamp(3rem, 6vw, 4.5rem)",
                fontWeight: 900,
                lineHeight: 0.95,
                color: "#ffffff",
                letterSpacing: "-0.04em",
                marginBottom: "10px"
              }}>
                  98<span style={{
                  color: GOLD
                }}>%</span>
                </div>
                <p style={{
                fontFamily: FONT_BODY,
                fontSize: "13.5px",
                lineHeight: 1.6,
                color: "rgba(255,255,255,0.7)"
              }}>
                  of all remote &amp; keypad jobs paired,
                  <br />
                  tested and signed off on the first visit.
                </p>

                {/* GG signature */}
                <div className="mt-6 pt-5 border-t flex items-center gap-3" style={{
                borderColor: "rgba(255,255,255,0.12)"
              }}>
                  <span style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "10px",
                  fontWeight: 800,
                  letterSpacing: "0.18em",
                  color: GOLD
                }}>
                    GG
                  </span>
                  <span style={{
                  fontFamily: FONT_BODY,
                  fontSize: "11px",
                  color: "rgba(255,255,255,0.5)",
                  letterSpacing: "0.05em"
                }}>
                    Based on 1,400+ Sydney callouts
                  </span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <a href="tel:0485000933" className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full transition-all hover:scale-[1.02]" style={{
            backgroundColor: NAVY,
            color: "#ffffff",
            fontFamily: FONT_HEAD,
            fontWeight: 700,
            fontSize: "14px",
            boxShadow: `0 8px 24px ${NAVY}33`
          }}>
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                <Phone size={16} strokeWidth={2.5} />
              </span>
              Call 0485 000 933
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                <ArrowRight size={14} strokeWidth={2.5} />
              </span>
            </a>
          </div>

          {/* RIGHT — ledger of 6 reasons */}
          <div>
            {/* Ledger header */}
            <div className="flex items-center justify-between pb-4 mb-2 border-b-2" style={{
            borderColor: NAVY
          }}>
              <span style={{
              fontFamily: FONT_HEAD,
              fontSize: "11px",
              fontWeight: 800,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: NAVY
            }}>
                The Gold guarantee — line by line
              </span>
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                <CheckCircle2 size={18} color={GOLD_DARK} strokeWidth={2.5} />
              </span>
            </div>

            {/* Reasons list */}
            <ul>
              {REASONS.map(({
              Icon,
              title,
              desc
            }, i) => <li key={title} className="group relative py-7 border-b transition-all" style={{
              borderColor: "rgba(8,20,56,0.12)"
            }}>
                  <div className="flex items-start gap-5">
                    {/* Number */}
                    <div className="flex-shrink-0" style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "44px",
                  fontWeight: 800,
                  lineHeight: 0.9,
                  color: GOLD,
                  letterSpacing: "-0.04em",
                  minWidth: "60px"
                }}>
                      {String(i + 1).padStart(2, "0")}
                    </div>

                    {/* Body */}
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-2">
                        <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-md flex-shrink-0 mt-0.5" style={{
                      width: 28,
                      height: 28,
                      backgroundColor: `${GOLD}18`,
                      border: `1px solid ${GOLD}33`
                    }}>
                          <Icon size={15} color={GOLD_DARK} strokeWidth={2.5} />
                        </span>
                        <h3 style={{
                      fontFamily: FONT_HEAD,
                      fontWeight: 800,
                      fontSize: "1.2rem",
                      lineHeight: 1.25,
                      color: NAVY,
                      letterSpacing: "-0.015em",
                      flex: 1
                    }}>
                          {title}
                        </h3>
                      </div>
                      <p style={{
                    fontFamily: FONT_BODY,
                    fontSize: "14.5px",
                    lineHeight: 1.65,
                    color: INK_SOFT,
                    marginLeft: "40px"
                  }}>
                        {desc}
                      </p>
                    </div>
                  </div>
                </li>)}
            </ul>

            {/* Footer signature */}
            <div className="pt-6 flex items-center justify-between">
              <span style={{
              fontFamily: FONT_BODY,
              fontSize: "12px",
              color: INK_SOFT,
              fontStyle: "italic"
            }}>
                Signed &amp; honoured by the Gold Garage Door team —
                <br />
                Bass Hill, NSW.
              </span>
              <span style={{
              fontFamily: FONT_HEAD,
              fontSize: "14px",
              fontWeight: 800,
              letterSpacing: "0.18em",
              color: GOLD_DARK
            }}>
                GG · EST 2009
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}